import type { NavigateFunction } from 'react-router-dom'
import { useRegisterFormState } from './useRegisterFormState'
import type { RegisterFormData } from './useRegisterValidation'
import { useRegisterValidation } from './useRegisterValidation'
import { useRegisterSubmission } from './useRegisterSubmission'

interface UseRegisterFormParams {
    /** Navigation function (required for dependency injection) */
    navigate: NavigateFunction
    /** Whether an invite key is required for registration */
    inviteKeyRequired?: boolean
}

interface UseRegisterFormReturn {
    formData: RegisterFormData
    error: string | null
    inviteKeyError: string | null
    loading: boolean
    showPassword: boolean
    showConfirmPassword: boolean
    touchedFields: {
        username: boolean
        email: boolean
        password: boolean
        confirmPassword: boolean
    }
    setFormData: React.Dispatch<React.SetStateAction<RegisterFormData>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    setInviteKeyError: React.Dispatch<React.SetStateAction<string | null>>
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleSelectChange: (e: { target: { name: string; value: string } }) => void
    handleTogglePasswordVisibility: () => void
    handleToggleConfirmPasswordVisibility: () => void
    handleSubmit: (e: React.FormEvent) => Promise<void>
}

export const useRegisterForm = ({
    navigate,
    inviteKeyRequired = false,
}: UseRegisterFormParams): UseRegisterFormReturn => {
    const formState = useRegisterFormState()
    const { validateForm } = useRegisterValidation()
    const { handleSubmit: submitForm } = useRegisterSubmission({
        formData: formState.formData,
        setError: formState.setError,
        setInviteKeyError: formState.setInviteKeyError,
        setLoading: formState.setLoading,
        navigate,
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        formState.setError('')
        formState.setInviteKeyError(null)

        const validation = validateForm(formState.formData, { inviteKeyRequired })
        if (!validation.isValid) {
            // Always surface a page-level alert so the failure is visible without
            // having to scroll back to the top; additionally route field-scoped
            // errors to the field so it's outlined and focused.
            formState.setError(validation.error)
            if (validation.field === 'inviteKey') {
                formState.setInviteKeyError(validation.error)
            }
            return
        }

        await submitForm(e)
    }

    return {
        formData: formState.formData,
        error: formState.error,
        inviteKeyError: formState.inviteKeyError,
        loading: formState.loading,
        showPassword: formState.showPassword,
        showConfirmPassword: formState.showConfirmPassword,
        touchedFields: formState.touchedFields,
        setFormData: formState.setFormData,
        setError: formState.setError,
        setInviteKeyError: formState.setInviteKeyError,
        handleChange: formState.handleChange,
        handleSelectChange: formState.handleSelectChange,
        handleTogglePasswordVisibility: formState.handleTogglePasswordVisibility,
        handleToggleConfirmPasswordVisibility: formState.handleToggleConfirmPasswordVisibility,
        handleSubmit,
    }
}
